/**
 * IMEI validation + candidate extraction (plan section 5).
 * Never silently accept a guessed digit: candidates carry a confidence level and the
 * UI requires explicit confirmation.
 */

/** Luhn check for a 15-digit IMEI. */
export function luhnImeiOk(imei: string): boolean {
  if (!/^\d{15}$/.test(imei)) return false
  let sum = 0
  for (let i = 0; i < 15; i++) {
    let d = Number(imei[i])
    if (i % 2 === 1) {
      d *= 2
      if (d > 9) d -= 9
    }
    sum += d
  }
  return sum % 10 === 0
}

/** A full 15-digit IMEI passing the Luhn checksum. */
export function isValidImei(imei: string): boolean {
  return /^\d{15}$/.test(imei) && luhnImeiOk(imei)
}

export type ImeiConfidence = 'high' | 'medium' | 'low'

export interface ImeiCandidate {
  value: string
  confidence: ImeiConfidence
}

/**
 * Pull plausible IMEI digit-runs (14-17) out of arbitrary scanned/OCR text.
 * 15 digits + valid Luhn = high; 14 (no check digit) or 16/17 (IMEISV) = medium;
 * 15 digits failing Luhn = low (likely a misread).
 */
export function extractImeiCandidates(text: string): ImeiCandidate[] {
  const runs = text.match(/\d{14,17}/g) ?? []
  const seen = new Set<string>()
  const out: ImeiCandidate[] = []
  for (const run of runs) {
    if (seen.has(run)) continue
    seen.add(run)
    let confidence: ImeiConfidence
    if (run.length === 15) confidence = luhnImeiOk(run) ? 'high' : 'low'
    else confidence = 'medium'
    out.push({ value: run, confidence })
  }
  const rank: Record<ImeiConfidence, number> = { high: 0, medium: 1, low: 2 }
  return out.sort((a, b) => rank[a.confidence] - rank[b.confidence])
}

/** Merge new candidates into an existing list, keeping the best confidence per value. */
export function mergeCandidates(existing: ImeiCandidate[], incoming: ImeiCandidate[]): ImeiCandidate[] {
  const rank: Record<ImeiConfidence, number> = { high: 0, medium: 1, low: 2 }
  const map = new Map<string, ImeiCandidate>()
  for (const c of [...existing, ...incoming]) {
    const prev = map.get(c.value)
    if (!prev || rank[c.confidence] < rank[prev.confidence]) map.set(c.value, c)
  }
  return [...map.values()].sort((a, b) => rank[a.confidence] - rank[b.confidence])
}
